import { Link } from "react-router-dom";

/**
 * Compact icon-tile grid menu for Manager/Director/Admin dashboard.
 * Renders themed group sections — each group has a colored header bar
 * with label, and a 6-column grid of tiles below.
 *
 * Items (each): { testid, to, href, external, onClick, icon, label, badge, accent, group }
 * Groups: { [groupKey]: { order, label, accent } }
 */

const HEADER_ACCENT = {
  green:  { dot: "bg-[#00E575]",   text: "text-[#00E575]",       line: "from-[#00E575]/40" },
  amber:  { dot: "bg-amber-400",   text: "text-amber-300",       line: "from-amber-500/40" },
  sky:    { dot: "bg-sky-400",     text: "text-sky-300",         line: "from-sky-500/40" },
  violet: { dot: "bg-violet-400",  text: "text-violet-200",      line: "from-violet-500/40" },
  red:    { dot: "bg-red-500",     text: "text-red-300",         line: "from-red-500/50" },
  zinc:   { dot: "bg-zinc-400",    text: "text-zinc-300",        line: "from-zinc-500/40" },
};

const Tile = ({ to, href, external, onClick, icon: Icon, label, badge, accent, testid }) => {
  const accentClass = {
    green:  "border-[#00E575]/40 hover:border-[#00E575] text-[#00E575]",
    amber:  "border-amber-500/40 hover:border-amber-400 text-amber-300",
    sky:    "border-sky-500/30 hover:border-sky-400 text-sky-300",
    violet: "border-violet-500/40 hover:border-violet-400 text-violet-200",
    red:    "border-red-500/40 hover:border-red-400 text-red-300",
  }[accent] || "border-zinc-700 hover:border-[#00E575]/60 text-zinc-200";

  const inner = (
    <div className="relative h-full flex flex-col items-center justify-center gap-1.5 py-2">
      <Icon size={20} strokeWidth={2.1} />
      <span className="text-[12px] font-medium leading-tight text-center px-1 line-clamp-2">{label}</span>
      {badge != null && badge > 0 && (
        <span className="absolute top-1 right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-[#09090B] text-[12px] font-bold">
          {badge}
        </span>
      )}
    </div>
  );

  const base = `block bg-[#18181B] border rounded-xl h-[78px] transition active:scale-[0.96] hover:bg-[#1F1F23] ${accentClass}`;

  if (to) return <Link to={to} data-testid={testid} className={base}>{inner}</Link>;
  if (href) return <a href={href} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined} data-testid={testid} className={base}>{inner}</a>;
  return <button type="button" onClick={onClick} data-testid={testid} className={`${base} w-full text-left`}>{inner}</button>;
};

const GroupHeader = ({ label, accent, testid }) => {
  const c = HEADER_ACCENT[accent] || HEADER_ACCENT.zinc;
  return (
    <div data-testid={testid} className="flex items-center gap-3 mt-5 mb-2 first:mt-0">
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${c.dot}`} />
      <h3 className={`font-mono text-[12px] tracking-[0.22em] uppercase font-semibold ${c.text}`}>{label}</h3>
      <div className={`flex-1 h-px bg-gradient-to-r ${c.line} to-transparent`} />
    </div>
  );
};

export const DashboardManagerNav = ({ items, groups = {} }) => {
  const cleanItems = (items || []).filter(Boolean);

  // Group items by their `group` key. Items without group end up in "_other".
  const byGroup = cleanItems.reduce((acc, it) => {
    const g = it.group || "_other";
    if (!acc[g]) acc[g] = [];
    acc[g].push(it);
    return acc;
  }, {});

  // Sort group keys by configured `order` then alphabetically.
  const sortedGroupKeys = Object.keys(byGroup).sort((a, b) => {
    const oa = groups[a]?.order ?? 99;
    const ob = groups[b]?.order ?? 99;
    if (oa !== ob) return oa - ob;
    return a.localeCompare(b);
  });

  // Fallback: no group definitions at all → render flat grid as before.
  if (Object.keys(groups).length === 0) {
    return (
      <div
        data-testid="dashboard-manager-nav-grid"
        className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mb-6"
      >
        {cleanItems.map((it, i) => (
          <Tile key={it.testid || i} {...it} />
        ))}
      </div>
    );
  }

  return (
    <div data-testid="dashboard-manager-nav-grouped" className="mb-6">
      {sortedGroupKeys.map((gk) => {
        const meta = groups[gk] || { label: gk, accent: "zinc" };
        const groupItems = byGroup[gk];
        if (!groupItems || groupItems.length === 0) return null;
        return (
          <div key={gk} data-testid={`nav-group-${gk}`}>
            <GroupHeader label={meta.label} accent={meta.accent} testid={`nav-group-header-${gk}`} />
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {groupItems.map((it, i) => (
                <Tile key={it.testid || `${gk}-${i}`} {...it} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
